import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, addDoc, deleteDoc, doc, getDoc, setDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';

const AnchorSVG = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 40 52" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="20" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
    <circle cx="20" cy="7" r="2" fill="currentColor"/>
    <line x1="4" y1="14" x2="36" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="20" y1="12" x2="20" y2="40" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M20 40 C13 38 5 33 5 26" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    <path d="M20 40 C27 38 35 33 35 26" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    <path d="M5 26 C5 46 35 46 35 26" stroke="currentColor" strokeWidth="1.5" fill="none"/>
  </svg>
);

interface RSVPData {
  id: string;
  guestId: string;
  guestName: string;
  familyId: string;
  email: string;
  phone: string;
  attending: 'yes' | 'no' | 'pending';
  attendingAfterparty?: 'yes' | 'no' | 'pending';
  dietaryRestrictions: string;
  danceSong: string;
  message: string;
  submittedAt: any;
}

interface GuestData {
  id: string;
  name: string;
  nameLowercase: string;
  familyId: string;
  familyName: string;
}

interface FamilyData {
  id: string;
  name: string;
}

interface QAItem {
  id: string;
  question: string;
  answer: string;
  order: number;
}

interface DetailsContent {
  weddingDate: string;
  venueName: string;
  venueDescription: string;
  venueAddress: string;
  venueCity: string;
  venueMapUrl: string;
}

interface TimelineEvent {
  time: string;
  title: string;
  description: string;
}

const RSVPResults: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check localStorage on initial load
    return localStorage.getItem('rsvpAdminAuth') === 'true';
  });
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [rsvps, setRsvps] = useState<RSVPData[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'yes' | 'no'>('all');

  const [activeTab, setActiveTab] = useState<'rsvps' | 'notrsvpd' | 'guestlist' | 'content'>('rsvps');
  const [guests, setGuests] = useState<GuestData[]>([]);
  const [families, setFamilies] = useState<FamilyData[]>([]);
  const [newGuestName, setNewGuestName] = useState('');
  const [newFamilyName, setNewFamilyName] = useState('');
  const [selectedFamilyId, setSelectedFamilyId] = useState('');
  const [addingGuest, setAddingGuest] = useState(false);
  const [addingFamily, setAddingFamily] = useState(false);

  // Content Editor State
  const [qaItems, setQAItems] = useState<QAItem[]>([]);
  const [detailsContent, setDetailsContent] = useState<DetailsContent>({
    weddingDate: 'Sunday, September 6th, 2026',
    venueName: 'Jefferson Beach Marina',
    venueDescription: 'The wedding will be held at the Jefferson Beach Marina.',
    venueAddress: '24400 Jefferson Avenue',
    venueCity: 'Saint Clair Shores, Michigan',
    venueMapUrl: 'https://www.google.com/maps/search/?api=1&query=Jefferson+Beach+Marina+24400+Jefferson+Avenue+Saint+Clair+Shores+Michigan'
  });
  const [timeline, setTimeline] = useState<TimelineEvent[]>([
    { time: '11:00 AM', title: 'Ceremony', description: 'Ceremony begins on the waterfront' },
    { time: '11:30 AM', title: 'Boarding', description: 'Board the Ovation Yacht for the reception' },
    { time: '12:00 PM', title: 'Cruise Departs', description: 'The yacht leaves promptly for a cruise on Lake St. Clair' },
    { time: '3:00 PM', title: 'Return to Dock', description: 'The yacht returns to the marina' }
  ]);
  const [savingContent, setSavingContent] = useState(false);
  const [contentSaved, setContentSaved] = useState(false);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (username === 'squishy' && password === 'ashley&a.j.') {
      setIsAuthenticated(true);
      localStorage.setItem('rsvpAdminAuth', 'true');
      setLoginError('');
    } else {
      setLoginError('Invalid username or password');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('rsvpAdminAuth');
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchRSVPs();
      fetchGuests();
      fetchFamilies();
      fetchContent();
    }
  }, [isAuthenticated]);

  const fetchFamilies = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'families'));
      const familyData: FamilyData[] = [];
      querySnapshot.forEach((doc) => {
        familyData.push({ id: doc.id, ...doc.data() } as FamilyData);
      });
      setFamilies(familyData.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Error fetching families:', error);
    }
  };

  const fetchGuests = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'guestList'));
      const guestData: GuestData[] = [];
      querySnapshot.forEach((doc) => {
        guestData.push({ id: doc.id, ...doc.data() } as GuestData);
      });
      setGuests(guestData.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Error fetching guests:', error);
    }
  };

  const handleAddFamily = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddingFamily(true);
    try {
      await addDoc(collection(db, 'families'), {
        name: newFamilyName.trim()
      });
      setNewFamilyName('');
      fetchFamilies();
    } catch (error) {
      console.error('Error adding family:', error);
    } finally {
      setAddingFamily(false);
    }
  };

  const handleAddGuest = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddingGuest(true);
    try {
      const family = selectedFamilyId ? families.find(f => f.id === selectedFamilyId) : null;
      await addDoc(collection(db, 'guestList'), {
        name: newGuestName.trim(),
        nameLowercase: newGuestName.toLowerCase().trim(),
        familyId: selectedFamilyId || '',
        familyName: family?.name || 'Individual'
      });
      setNewGuestName('');
      setSelectedFamilyId('');
      fetchGuests();
    } catch (error) {
      console.error('Error adding guest:', error);
    } finally {
      setAddingGuest(false);
    }
  };

  const handleDeleteGuest = async (guestId: string) => {
    if (window.confirm('Are you sure you want to remove this guest from the list?')) {
      try {
        await deleteDoc(doc(db, 'guestList', guestId));
        fetchGuests();
      } catch (error) {
        console.error('Error deleting guest:', error);
      }
    }
  };

  const handleDeleteFamily = async (familyId: string) => {
    const familyGuests = guests.filter(g => g.familyId === familyId);
    if (familyGuests.length > 0) {
      alert('Cannot delete family with guests. Please remove all guests first.');
      return;
    }
    if (window.confirm('Are you sure you want to remove this family?')) {
      try {
        await deleteDoc(doc(db, 'families', familyId));
        fetchFamilies();
      } catch (error) {
        console.error('Error deleting family:', error);
      }
    }
  };

  const fetchRSVPs = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'rsvps'), orderBy('submittedAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const rsvpData: RSVPData[] = [];
      querySnapshot.forEach((doc) => {
        rsvpData.push({ id: doc.id, ...doc.data() } as RSVPData);
      });
      setRsvps(rsvpData);
    } catch (error) {
      console.error('Error fetching RSVPs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContent = async () => {
    try {
      // Fetch Q&A items
      const qaQuery = query(collection(db, 'qaItems'), orderBy('order', 'asc'));
      const qaSnapshot = await getDocs(qaQuery);
      const qaData: QAItem[] = [];
      qaSnapshot.forEach((doc) => {
        qaData.push({ id: doc.id, ...doc.data() } as QAItem);
      });
      if (qaData.length > 0) {
        setQAItems(qaData);
      }

      // Fetch Details content
      const detailsDoc = await getDoc(doc(db, 'siteContent', 'details'));
      if (detailsDoc.exists()) {
        setDetailsContent(detailsDoc.data() as DetailsContent);
      }

      // Fetch Timeline
      const timelineDoc = await getDoc(doc(db, 'siteContent', 'timeline'));
      if (timelineDoc.exists()) {
        const data = timelineDoc.data();
        if (data.events) {
          setTimeline(data.events);
        }
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const saveContent = async () => {
    setSavingContent(true);
    setContentSaved(false);
    try {
      const batch = writeBatch(db);

      // Delete existing Q&A items
      const existingQA = await getDocs(collection(db, 'qaItems'));
      existingQA.forEach((docSnap) => {
        batch.delete(docSnap.ref);
      });

      // Add updated Q&A items
      qaItems.forEach((item, index) => {
        const qaRef = doc(collection(db, 'qaItems'));
        batch.set(qaRef, {
          question: item.question,
          answer: item.answer,
          order: index
        });
      });

      // Save Details content
      batch.set(doc(db, 'siteContent', 'details'), detailsContent);

      // Save Timeline
      batch.set(doc(db, 'siteContent', 'timeline'), { events: timeline });

      await batch.commit();
      setContentSaved(true);
      setTimeout(() => setContentSaved(false), 3000);
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Error saving content. Please try again.');
    } finally {
      setSavingContent(false);
    }
  };

  const filteredRsvps = rsvps.filter(rsvp => {
    if (rsvp.attending === 'pending') return false;
    if (filter === 'all') return true;
    return rsvp.attending === filter;
  });

  const attendingCount = rsvps.filter(r => r.attending === 'yes').length;
  const notAttendingCount = rsvps.filter(r => r.attending === 'no').length;
  const confirmedCount = attendingCount + notAttendingCount;

  const PageHeader = ({ eyebrow, title }: { eyebrow: string; title: string }) => (
    <div className="page-header">
      <AnchorSVG className="page-header__anchor" />
      <p className="page-header__eyebrow">{eyebrow}</p>
      <h1 className="page-header__title">{title}</h1>
      <div className="page-header__rule page-rule">
        <div className="rule-line rule-line--short"></div>
        <div className="rule-diamond"></div>
        <div className="rule-line rule-line--short"></div>
      </div>
    </div>
  );

  if (!isAuthenticated) {
    return (
      <main>
        <PageHeader eyebrow="Admin" title="Sign In" />
        <section className="rsvp-section">
          <div className="rsvp-container">
            <div className="rsvp-search-card">
              <form onSubmit={handleLogin} className="rsvp-search-form">
                <div className="form-group">
                  <label htmlFor="username">Username</label>
                  <input type="text" id="username" value={username} onChange={(e) => setUsername(e.target.value)} required autoComplete="username" />
                </div>
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
                </div>
                {loginError && <div className="error-message">{loginError}</div>}
                <button type="submit" className="cta-button">Sign In</button>
              </form>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main>
      <PageHeader eyebrow="Admin" title="Dashboard" />

      <section className="admin-section">
        <nav className="admin-tabs">
          <button className={`admin-tab${activeTab === 'rsvps' ? ' admin-tab--active' : ''}`} onClick={() => setActiveTab('rsvps')}>RSVP Results</button>
          <button className={`admin-tab${activeTab === 'notrsvpd' ? ' admin-tab--active' : ''}`} onClick={() => setActiveTab('notrsvpd')}>Not RSVPd</button>
          <button className={`admin-tab${activeTab === 'guestlist' ? ' admin-tab--active' : ''}`} onClick={() => setActiveTab('guestlist')}>Guest List ({guests.length})</button>
          <button className={`admin-tab${activeTab === 'content' ? ' admin-tab--active' : ''}`} onClick={() => setActiveTab('content')}>Content Editor</button>
          <a href="/rsvp-admin" className="admin-tab">RSVP for Guest</a>
        </nav>

        <div className="admin-content">

          {/* ── RSVP RESULTS ── */}
          {activeTab === 'rsvps' && (
            <>
              <div className="admin-stats">
                <div className="admin-stat">
                  <p className="admin-stat__label">Total RSVPs</p>
                  <p className="admin-stat__number">{confirmedCount}</p>
                </div>
                <div className="admin-stat">
                  <p className="admin-stat__label">Attending</p>
                  <p className="admin-stat__number admin-stat__number--attending">{attendingCount}</p>
                </div>
                <div className="admin-stat">
                  <p className="admin-stat__label">Not Attending</p>
                  <p className="admin-stat__number admin-stat__number--absent">{notAttendingCount}</p>
                </div>
              </div>

              <div className="admin-filters">
                <button className={`admin-filter${filter === 'all' ? ' admin-filter--active' : ''}`} onClick={() => setFilter('all')}>All ({confirmedCount})</button>
                <button className={`admin-filter admin-filter--attending${filter === 'yes' ? ' admin-filter--active' : ''}`} onClick={() => setFilter('yes')}>Attending ({attendingCount})</button>
                <button className={`admin-filter admin-filter--absent${filter === 'no' ? ' admin-filter--active' : ''}`} onClick={() => setFilter('no')}>Not Attending ({notAttendingCount})</button>
              </div>

              {loading ? (
                <p style={{textAlign: 'center', color: 'var(--muted)', letterSpacing: '3px', textTransform: 'uppercase', fontSize: '0.58rem'}}>Loading...</p>
              ) : filteredRsvps.length === 0 ? (
                <p style={{textAlign: 'center', color: 'var(--muted)'}}>No RSVPs yet.</p>
              ) : filteredRsvps.map((rsvp) => (
                <div key={rsvp.id} className={`admin-rsvp-card admin-rsvp-card--${rsvp.attending}`}>
                  <div className="admin-rsvp-card__header">
                    <h3 className="admin-rsvp-card__name">{rsvp.guestName}</h3>
                    <span className={`admin-badge admin-badge--${rsvp.attending}`}>
                      {rsvp.attending === 'yes' ? 'Attending' : 'Not Attending'}
                    </span>
                  </div>
                  <p className="admin-rsvp-card__detail"><strong>Email:</strong> {rsvp.email || '—'}</p>
                  {rsvp.phone && <p className="admin-rsvp-card__detail"><strong>Phone:</strong> {rsvp.phone}</p>}
                  {rsvp.attending === 'yes' && (
                    <>
                      <p className="admin-rsvp-card__detail"><strong>Dietary Restrictions:</strong> {rsvp.dietaryRestrictions || '—'}</p>
                      {rsvp.attendingAfterparty && (
                        <p className="admin-rsvp-card__detail"><strong>Afterparty:</strong> {rsvp.attendingAfterparty === 'yes' ? 'Yes' : rsvp.attendingAfterparty === 'no' ? 'No' : 'Not sure yet'}</p>
                      )}
                      {rsvp.danceSong && <p className="admin-rsvp-card__detail"><strong>Dance Song:</strong> {rsvp.danceSong}</p>}
                    </>
                  )}
                  {rsvp.message && <p className="admin-rsvp-card__detail"><strong>Message:</strong> {rsvp.message}</p>}
                  <p className="admin-rsvp-card__time">Submitted: {rsvp.submittedAt?.toDate?.()?.toLocaleString() || 'N/A'}</p>
                </div>
              ))}
            </>
          )}

          {/* ── NOT RSVPd ── */}
          {activeTab === 'notrsvpd' && (() => {
            const rsvpdGuestIds = new Set(rsvps.map(r => r.guestId));
            const notRsvpd = guests.filter(g => !rsvpdGuestIds.has(g.id));
            const byFamily = notRsvpd.reduce<Record<string, GuestData[]>>((acc, g) => {
              const key = g.familyName || 'Individual';
              if (!acc[key]) acc[key] = [];
              acc[key].push(g);
              return acc;
            }, {});
            return (
              <>
                <div className="admin-stats">
                  <div className="admin-stat">
                    <p className="admin-stat__label">Not Yet RSVPd</p>
                    <p className="admin-stat__number">{notRsvpd.length}</p>
                  </div>
                  <div className="admin-stat">
                    <p className="admin-stat__label">Total Guests</p>
                    <p className="admin-stat__number">{guests.length}</p>
                  </div>
                </div>
                {notRsvpd.length === 0 ? (
                  <p style={{textAlign: 'center', color: 'var(--muted)'}}>Everyone has RSVPd!</p>
                ) : Object.entries(byFamily).sort(([a], [b]) => a.localeCompare(b)).map(([family, members]) => (
                  <div key={family} className="admin-guest-family">
                    <div className="admin-guest-family__header">
                      <span className="admin-guest-family__name">{family}</span>
                    </div>
                    {members.map(g => (
                      <div key={g.id} className="admin-guest-row">
                        <span>{g.name}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </>
            );
          })()}

          {/* ── GUEST LIST ── */}
          {activeTab === 'guestlist' && (
            <>
              <div className="admin-form-card">
                <h3 className="admin-form-card__title">Add New Family</h3>
                <form onSubmit={handleAddFamily}>
                  <div className="form-group">
                    <label htmlFor="familyName">Family Name *</label>
                    <input type="text" id="familyName" value={newFamilyName} onChange={(e) => setNewFamilyName(e.target.value)} required placeholder="e.g., Smith Family" />
                  </div>
                  <button type="submit" className="cta-button" disabled={addingFamily}>{addingFamily ? 'Adding...' : 'Add Family'}</button>
                </form>
              </div>

              <div className="admin-form-card">
                <h3 className="admin-form-card__title">Add New Guest</h3>
                <form onSubmit={handleAddGuest}>
                  <div className="form-group">
                    <label htmlFor="familySelect">Family <span style={{fontSize: '0.8rem', fontWeight: 'normal', color: 'var(--muted)'}}>— optional</span></label>
                    <select id="familySelect" value={selectedFamilyId} onChange={(e) => setSelectedFamilyId(e.target.value)}>
                      <option value="">No family (individual guest)</option>
                      {families.map(family => <option key={family.id} value={family.id}>{family.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="guestName">Guest Name *</label>
                    <input type="text" id="guestName" value={newGuestName} onChange={(e) => setNewGuestName(e.target.value)} required placeholder="Enter guest's full name" />
                  </div>
                  <button type="submit" className="cta-button" disabled={addingGuest}>{addingGuest ? 'Adding...' : 'Add Guest'}</button>
                </form>
              </div>

              <h3 className="admin-section-title">Families ({families.length})</h3>
              {families.length === 0 ? (
                <p style={{textAlign: 'center', color: 'var(--muted)'}}>No families added yet.</p>
              ) : families.map((family) => {
                const familyGuests = guests.filter(g => g.familyId === family.id);
                return (
                  <div key={family.id} className="admin-guest-family">
                    <div className="admin-guest-family__header">
                      <span className="admin-guest-family__name">{family.name}</span>
                      <button onClick={() => handleDeleteFamily(family.id)} className="admin-delete-btn">Remove</button>
                    </div>
                    {familyGuests.length === 0 ? (
                      <p style={{margin: 0, color: 'var(--muted)', fontSize: '0.85rem'}}>No guests in this family</p>
                    ) : familyGuests.map((guest) => (
                      <div key={guest.id} className="admin-guest-row">
                        <span>{guest.name}</span>
                        <button onClick={() => handleDeleteGuest(guest.id)} className="admin-delete-btn">Remove</button>
                      </div>
                    ))}
                  </div>
                );
              })}

              <h3 className="admin-section-title" style={{marginTop: '2.5rem'}}>Individual Guests ({guests.filter(g => !g.familyId).length})</h3>
              {guests.filter(g => !g.familyId).length === 0 ? (
                <p style={{textAlign: 'center', color: 'var(--muted)'}}>No individual guests yet.</p>
              ) : guests.filter(g => !g.familyId).map((guest) => (
                <div key={guest.id} className="admin-guest-row">
                  <span>{guest.name}</span>
                  <button onClick={() => handleDeleteGuest(guest.id)} className="admin-delete-btn">Remove</button>
                </div>
              ))}
            </>
          )}

          {/* ── CONTENT EDITOR ── */}
          {activeTab === 'content' && (
            <>
              {contentSaved && <div className="admin-save-success">Content saved successfully!</div>}

              <div className="admin-form-card">
                <h3 className="admin-form-card__title">Q&amp;A Section</h3>
                <div className="admin-tips">
                  <strong>Formatting Tips:</strong>
                  <ul>
                    <li>Line breaks: Press Enter twice for new paragraphs</li>
                    <li>Links: [Link text](https://example.com)</li>
                    <li>Bold: **bold text**</li>
                    <li>Italic: *italic text*</li>
                    <li>Lists: Start lines with - for bullet points</li>
                  </ul>
                </div>
                {qaItems.map((item, index) => (
                  <div key={index} style={{marginBottom: '1.5rem', padding: '1rem', background: 'var(--white)', border: '1px solid var(--gold-dim)'}}>
                    <div className="form-group">
                      <label>Question</label>
                      <input type="text" value={item.question} onChange={(e) => { const updated = [...qaItems]; updated[index].question = e.target.value; setQAItems(updated); }} style={{width: '100%'}} />
                    </div>
                    <div className="form-group">
                      <label>Answer</label>
                      <textarea value={item.answer} onChange={(e) => { const updated = [...qaItems]; updated[index].answer = e.target.value; setQAItems(updated); }} rows={5} style={{width: '100%', fontFamily: 'monospace', fontSize: '0.9rem'}} placeholder="Supports markdown formatting..." />
                    </div>
                    <button onClick={() => setQAItems(qaItems.filter((_, i) => i !== index))} className="admin-delete-btn">Remove Question</button>
                  </div>
                ))}
                <button onClick={() => setQAItems([...qaItems, { id: Date.now().toString(), question: '', answer: '', order: qaItems.length }])} className="cta-button" style={{marginTop: '1rem'}}>Add Question</button>
              </div>

              <div className="admin-form-card">
                <h3 className="admin-form-card__title">Wedding Details</h3>
                <div className="form-group">
                  <label>Wedding Date</label>
                  <input type="text" value={detailsContent.weddingDate} onChange={(e) => setDetailsContent({...detailsContent, weddingDate: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Venue Name</label>
                  <input type="text" value={detailsContent.venueName} onChange={(e) => setDetailsContent({...detailsContent, venueName: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Venue Description</label>
                  <textarea value={detailsContent.venueDescription} onChange={(e) => setDetailsContent({...detailsContent, venueDescription: e.target.value})} rows={2} />
                </div>
                <div className="form-group">
                  <label>Venue Address</label>
                  <input type="text" value={detailsContent.venueAddress} onChange={(e) => setDetailsContent({...detailsContent, venueAddress: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Venue City</label>
                  <input type="text" value={detailsContent.venueCity} onChange={(e) => setDetailsContent({...detailsContent, venueCity: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Google Maps URL</label>
                  <input type="text" value={detailsContent.venueMapUrl} onChange={(e) => setDetailsContent({...detailsContent, venueMapUrl: e.target.value})} />
                </div>
              </div>

              <div className="admin-form-card">
                <h3 className="admin-form-card__title">Timeline</h3>
                {timeline.map((event, index) => (
                  <div key={index} style={{marginBottom: '1.5rem', padding: '1rem', background: 'var(--white)', border: '1px solid var(--gold-dim)'}}>
                    <div className="form-group">
                      <label>Time</label>
                      <input type="text" value={event.time} onChange={(e) => { const updated = [...timeline]; updated[index].time = e.target.value; setTimeline(updated); }} placeholder="e.g., 11:00 AM" />
                    </div>
                    <div className="form-group">
                      <label>Title</label>
                      <input type="text" value={event.title} onChange={(e) => { const updated = [...timeline]; updated[index].title = e.target.value; setTimeline(updated); }} />
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <textarea value={event.description} onChange={(e) => { const updated = [...timeline]; updated[index].description = e.target.value; setTimeline(updated); }} rows={2} />
                    </div>
                    <button onClick={() => setTimeline(timeline.filter((_, i) => i !== index))} className="admin-delete-btn">Remove Event</button>
                  </div>
                ))}
                <button onClick={() => setTimeline([...timeline, { time: '', title: '', description: '' }])} className="cta-button" style={{marginTop: '1rem'}}>Add Timeline Event</button>
              </div>

              <div style={{textAlign: 'center'}}>
                <button onClick={saveContent} className="cta-button" disabled={savingContent} style={{padding: '1rem 3rem'}}>
                  {savingContent ? 'Saving...' : 'Save All Content'}
                </button>
              </div>
            </>
          )}

        </div>

        <div className="admin-logout">
          <a href="/rsvp-admin" className="cta-button" style={{ display: 'inline-block', marginBottom: '1rem', textDecoration: 'none' }}>RSVP for Guest</a>
          <br />
          <button onClick={handleLogout} className="cta-button">Sign Out</button>
        </div>
      </section>
    </main>
  );
};

export default RSVPResults;
